import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Bag, Cube, ShoppingCart, StackSimple, Waves } from '@phosphor-icons/react';
import { Loader } from 'semantic-ui-react';
import { theme, WaveService } from '../../utility';

const ICON_PROPS = { size: 20, color: '#aaa', style: { marginRight: 5 } };

export default function WaveSummaryBar({ warehouseId, status, analyticsParams }) {
  const { startDate, endDate } = analyticsParams || {};
  const { pathname } = useLocation();

  const isAnalyticsTab = pathname.includes('analytics');

  const effectiveStatus = useMemo(() => {
    if (isAnalyticsTab) return analyticsParams?.status || null;
    if (!status) return null;
    if (Array.isArray(status)) return status[0] ?? null;
    return status || null;
  }, [isAnalyticsTab, analyticsParams?.status, status]);

  const analyticsStart = useMemo(
    () => (isAnalyticsTab ? analyticsParams?.startDate || null : null),
    [isAnalyticsTab, analyticsParams?.startDate],
  );

  const analyticsEnd = useMemo(
    () => (isAnalyticsTab ? analyticsParams?.endDate || null : null),
    [isAnalyticsTab, analyticsParams?.endDate],
  );

  const formatDate = date =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!warehouseId) return;
    if (isAnalyticsTab && (!analyticsStart || !analyticsEnd)) {
      setSummary(null);
      return;
    }

    let isCancelled = false;

    const fetchSummary = async () => {
      setLoading(true);
      try {
        if (!effectiveStatus) return;

        const params = {
          warehouseId,
          status: effectiveStatus,
        };

        if (analyticsStart) params.startDate = new Date(analyticsStart).toISOString();
        if (analyticsEnd) params.endDate = new Date(analyticsEnd).toISOString();

        const res = await WaveService.orderWavesSummary(params);
        if (!isCancelled) {
          setSummary(res?.summary);
        }
      } catch (err) {
        if (!isCancelled) console.error('[Wave Summary Error]', err);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchSummary();

    return () => {
      isCancelled = true;
    };
  }, [warehouseId, effectiveStatus, analyticsStart, analyticsEnd, isAnalyticsTab]);

  const formatValue = key => {
    if (
      loading ||
      summary === null ||
      summary?.[key] === undefined ||
      summary?.[key] === null
    ) {
      return '--';
    }

    const value = summary[key];
    return typeof value === 'number' ? value.toLocaleString() : '--';
  };

  const summaryItems = [
    { icon: <Cube {...ICON_PROPS} />, label: 'Units', key: 'totalUnits' },
    {
      icon: <ShoppingCart {...ICON_PROPS} />,
      label: 'Orders',
      key: 'totalOrders',
    },
    { icon: <Bag {...ICON_PROPS} />, label: 'Totes', key: 'totalTotes' },
    {
      icon: <StackSimple {...ICON_PROPS} />,
      label: 'Pallets',
      key: 'totalPallets',
    },
    { icon: <Waves {...ICON_PROPS} />, label: 'Waves', key: 'totalWaves' },
  ];

  if (pathname.includes('unassigned') || pathname.includes('sorted')) return null;

  return (
    <Container>
      {pathname.includes('analytics') && startDate && (
        <DateRangeInfo>
          Showing results from <strong>{formatDate(startDate)}</strong> to{' '}
          <strong>{endDate ? formatDate(endDate) : 'Set End Date'}</strong>
        </DateRangeInfo>
      )}
      <SummaryWrapper>
        <Row>
          {summaryItems.map(({ icon, label, key }) => (
            <StatBox key={key}>
              <LabelGroup>
                {icon}
                <LabelText>{label}</LabelText>
              </LabelGroup>
              {loading ? (
                <Loader active inline size="tiny" />
              ) : (
                <ValueText>{formatValue(key)}</ValueText>
              )}
            </StatBox>
          ))}
        </Row>
      </SummaryWrapper>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  padding-top: 10px;
  position: relative;
`;

const DateRangeInfo = styled.div`
  width: 100%;
  text-align: start;
  font-size: 13px;
  color: ${theme.colors.grey};
  margin-bottom: 8px;
  text-align: center;
`;

const SummaryWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 10px;
`;

const StatBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 10px;
  flex: 1;
  height: 45px;
  padding: 0 10px;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: ${theme.colors.white};
`;

const LabelGroup = styled.div`
  display: flex;
  align-items: center;
`;

const LabelText = styled.span`
  color: ${theme.colors.black};
  font-size: 14px;
  font-weight: 300;
`;

const ValueText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${theme.colors.black};
  min-height: 20px;
`;
